/**
 * Web3 and MetaMask utility functions
 * Handles MetaMask detection and connection errors gracefully
 */

// Check if MetaMask is installed
export const isMetaMaskInstalled = () => {
  return typeof window !== 'undefined' && window.ethereum && window.ethereum.isMetaMask;
};

// Check if MetaMask is connected
export const isMetaMaskConnected = async () => {
  if (!isMetaMaskInstalled()) {
    return false;
  }
  
  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    return accounts.length > 0;
  } catch (error) {
    console.warn('Error checking MetaMask connection:', error);
    return false;
  }
};

// Connect to MetaMask with error handling
export const connectToMetaMask = async () => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
  }

  try {
    const accounts = await window.ethereum.request({ 
      method: 'eth_requestAccounts' 
    });
    return accounts;
  } catch (error) {
    if (error.code === 4001) {
      throw new Error('User rejected the connection request.');
    } else if (error.code === -32002) {
      throw new Error('MetaMask connection request already pending. Please check your MetaMask extension.');
    } else {
      throw new Error(`Failed to connect to MetaMask: ${error.message}`);
    }
  }
};

// Get current account
export const getCurrentAccount = async () => {
  if (!isMetaMaskInstalled()) {
    return null;
  }

  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    return accounts[0] || null;
  } catch (error) {
    console.warn('Error getting current account:', error);
    return null;
  }
};

// Listen for account changes
export const onAccountsChanged = (callback) => {
  if (!isMetaMaskInstalled()) {
    return;
  }

  window.ethereum.on('accountsChanged', callback);
};

// Listen for chain changes
export const onChainChanged = (callback) => {
  if (!isMetaMaskInstalled()) {
    return;
  }

  window.ethereum.on('chainChanged', callback);
};

// Remove event listeners
export const removeMetaMaskListeners = () => {
  if (!isMetaMaskInstalled()) {
    return;
  }

  window.ethereum.removeAllListeners('accountsChanged');
  window.ethereum.removeAllListeners('chainChanged');
};

// Suppress MetaMask connection errors in console
export const suppressMetaMaskErrors = () => {
  if (typeof window === 'undefined') return;

  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalLog = console.log;

  // Override console.error
  console.error = (...args) => {
    const errorMessage = args.join(' ');
    
    // Suppress MetaMask connection errors
    if (errorMessage.includes('Failed to connect to MetaMask') || 
        errorMessage.includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn') ||
        errorMessage.includes('inpage.js') ||
        errorMessage.includes('MetaMask') ||
        errorMessage.includes('ethereum')) {
      // Silently ignore MetaMask errors
      return;
    }
    
    // Log other errors normally
    originalError.apply(console, args);
  };

  // Override console.warn
  console.warn = (...args) => {
    const warnMessage = args.join(' ');
    
    // Suppress MetaMask warnings
    if (warnMessage.includes('MetaMask') || 
        warnMessage.includes('ethereum') ||
        warnMessage.includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn')) {
      return;
    }
    
    originalWarn.apply(console, args);
  };

  // Override console.log
  console.log = (...args) => {
    const logMessage = args.join(' ');
    
    // Suppress MetaMask logs
    if (logMessage.includes('MetaMask') || 
        logMessage.includes('ethereum') ||
        logMessage.includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn')) {
      return;
    }
    
    originalLog.apply(console, args);
  };

  // Also handle unhandled promise rejections related to MetaMask
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && 
        (event.reason.toString().includes('MetaMask') ||
         event.reason.toString().includes('Failed to connect') ||
         event.reason.toString().includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn'))) {
      event.preventDefault();
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    if (event.message && 
        (event.message.includes('MetaMask') ||
         event.message.includes('Failed to connect') ||
         event.message.includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn'))) {
      event.preventDefault();
    }
  });
};

